'use client'

import { Badge } from '@moment/design-system-ui/badge'
import React, { useCallback, useMemo } from 'react'

import { Routes } from '@/app/routes'
import { BodySubtext, BodyXS } from '@/components/atomic/text'
import {
  type ComplianceStatus,
  ComplianceStatusBadge,
} from '@/components/molecules/badges/compliance-status'
import {
  ComplianceItemContainer,
  HeaderContainer,
  LabelContainer,
  ViolationDetails,
  ViolationItemContainer,
  ViolationsButton,
  ViolationsContainer,
  ViolationsHeaderContainer,
  ViolationsListContainer,
} from '@/components/molecules/compliance-item/styles'
import { ModelConstraintType } from '@/types/api/constraints'
import { ComplianceLabels } from '@/types/model-compliance'

interface ViolationDetail {
  isin: string
  description: string
  weight: number
  market_value?: number
  note?: string
}

interface BaseComplianceItemProps {
  label: string
  value: string | number | React.ReactNode
  status?: ComplianceStatus
  type?: ModelConstraintType
  className?: string
  hasViolations?: boolean
  violationCount?: number
  violations?: ViolationDetail[]
  isLast?: boolean
  hidePopout?: boolean
  isMarketValueConstraint?: boolean
}

export function ComplianceItem({
  label,
  value,
  status,
  type,
  className,
  isLast,
  hasViolations = false,
  violationCount = 0,
  violations = [],
  hidePopout = false,
  isMarketValueConstraint = false,
}: BaseComplianceItemProps) {
  const isReactNode = React.isValidElement(value)
  const [isExpanded, setIsExpanded] = React.useState(false)

  const shouldShowViolations = useMemo(
    () => hasViolations && violations.length > 0 && isExpanded,
    [hasViolations, violations.length, isExpanded],
  )

  const sortedViolations = useMemo(
    () => [...violations].sort((a, b) => b.weight - a.weight),
    [violations],
  )

  const renderValue = useMemo(() => {
    if (isReactNode) return null

    const renderTickerBadges = (tickerValue: string | number) => (
      <div className="flex flex-wrap gap-1">
        {String(tickerValue)
          .split(',')
          .map((ticker, index) => (
            <Badge key={index} className="uppercase">
              {ticker.trim()}
            </Badge>
          ))}
      </div>
    )

    if (
      type === ModelConstraintType.Universe ||
      type === ModelConstraintType.BuyUniverse
    ) {
      switch (label) {
        case ComplianceLabels.Ticker:
          return renderTickerBadges(value as string | number)
        case ComplianceLabels.Cusip:
          return (
            <BodySubtext>{String(value).split(',').length} CUSIPs</BodySubtext>
          )
        case ComplianceLabels.Currency:
          return (
            <BodySubtext>
              {Array.isArray(value) ? value.join(', ') : value}
            </BodySubtext>
          )
        // biome-ignore lint/complexity/noUselessSwitchCase: Bulk suppression as part of biome migration
        case ComplianceLabels.Isin:
        // biome-ignore lint/complexity/noUselessSwitchCase: Bulk suppression as part of biome migration
        case ComplianceLabels.Instrument:
        default:
          return <BodySubtext>{value}</BodySubtext>
      }
    }

    return <BodySubtext>{value}</BodySubtext>
  }, [isReactNode, label, value, type])

  const handleBondDetailsClick = useCallback(
    (isin: string) => (e: React.MouseEvent<HTMLDivElement>) => {
      e.preventDefault()
      const popoutUrl = `${window.location.origin}${Routes.Research.detail({ bondId: isin })}`
      window.open(popoutUrl, '_blank', 'noopener,noreferrer')
    },
    [],
  )

  const handleViolationsClick = useCallback(() => {
    setIsExpanded((prev) => !prev)
  }, [])

  // Format violation values based on constraint type
  const formattedViolations = useMemo(() => {
    if (!violations?.length) return []

    return violations.map((violation) => ({
      ...violation,
      // For diversification, multiply weight by 100 if not using market value
      displayWeight:
        type === ModelConstraintType.Diversification && !isMarketValueConstraint
          ? violation.weight * 100
          : violation.weight,
      // Include market value if available and this is a market value constraint
      displayMarketValue: isMarketValueConstraint
        ? violation.market_value
        : undefined,
    }))
  }, [violations, type, isMarketValueConstraint])

  return (
    <ComplianceItemContainer isLast={isLast} className={className}>
      <HeaderContainer isReactNode={isReactNode}>
        <LabelContainer hasViolations={hasViolations}>
          <div>
            <BodyXS className="text-secondary">{label}</BodyXS>
            {renderValue}
          </div>
          {hasViolations && (
            <ViolationsContainer>
              <ViolationsButton
                violationCount={violationCount}
                isExpanded={isExpanded}
                onClick={handleViolationsClick}
                shouldShowViolations={violations.length > 0}
              />
            </ViolationsContainer>
          )}
        </LabelContainer>
        {status && <ComplianceStatusBadge status={status} />}
      </HeaderContainer>
      {isReactNode && <div className="w-full">{value}</div>}
      {shouldShowViolations && (
        <ViolationsListContainer>
          {!hidePopout && (
            <ViolationsHeaderContainer>
              <BodyXS className="text-secondary">Violated holdings</BodyXS>
              <BodyXS className="text-secondary">
                {isMarketValueConstraint ? 'Market Value' : 'Weight'}
              </BodyXS>
            </ViolationsHeaderContainer>
          )}
          {sortedViolations.map((violation, index) => {
            const formattedViolation = formattedViolations.find(
              (v) => v.isin === violation.isin,
            )
            return (
              <ViolationItemContainer
                key={violation.isin}
                isLast={index === sortedViolations.length - 1}
              >
                <ViolationDetails
                  isin={violation.isin}
                  weight={formattedViolation?.displayWeight || violation.weight}
                  marketValue={formattedViolation?.displayMarketValue}
                  description={violation.description}
                  note={violation.note}
                  onBondDetailsClick={handleBondDetailsClick}
                  hidePopout={hidePopout}
                  isMarketValueConstraint={isMarketValueConstraint}
                />
              </ViolationItemContainer>
            )
          })}
        </ViolationsListContainer>
      )}
    </ComplianceItemContainer>
  )
}
